/**
 * SLDViewerModal — Fullscreen Single Line Diagram viewer with pan/zoom,
 * data-point & advanced-rule overlay, side panel for placement, and
 * bidirectional alarm-state sync with the map markers.
 *
 * Interaction logic follows the reference impl in editor (23).html:
 *  - Pan: mousedown on container, mousemove/mouseup on window
 *  - Zoom: native wheel listener with { passive: false }
 *  - Placement: capture-phase click listener on container
 *  - Point drag: mousedown on point (stopPropagation), mousemove/mouseup on window
 *  - Click vs drag: tracked via DRAG_THRESHOLD pixel distance
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useMonitoringStore, useMapStore } from '@/stores';
import { useUIStore } from '@/stores/uiStore';
import { useProjectStore } from '@/stores/projectStore';
import { useAuthStore } from '@/stores/authStore';
import { uploadAsset } from '@/services/assetApi';
import { saveProject } from '@/services/projectApi';
import DataPointConfigModal from './DataPointConfigModal';
import PointDetailPopup from './sld/PointDetailPopup';
export { SLDNewWindowPortal } from './sld/SLDNewWindowPortal';
export { SLDStandaloneViewer } from './sld/SLDStandaloneViewer';
import MultiViewContainer from './sld/MultiViewContainer';
import SLDSidePanel from './sld/SLDSidePanel';
import SLDHeader from './sld/SLDHeader';
import type { AlarmState } from '@/types/monitoring';
import type { SLDPointMapping, SLDAdvancedRuleMapping, SLDDiagram } from '@/stores/monitoringStore';
import {
  EMPTY_DATAPOINTS,
  EMPTY_ADVANCED_RULES,
  EMPTY_MAPPINGS,
  EMPTY_RULE_MAPPINGS,
  EMPTY_TRIGGERED,
  DRAG_THRESHOLD,
  stateClass,
  stateTextClass,
  clamp,
  renderMetricValues,
  type PlacementType,
  type SidePanelTab,
} from './sld/sldUtils';
import './SLDViewerModal.css';

// ────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────

export default function SLDViewerModal() {
  // ── Store selectors (individual per convention) ──
  const sldDiagrams = useMonitoringStore((s) => s.sldDiagrams);
  const addSLD = useMonitoringStore((s) => s.addSLD);
  const updateSLD = useMonitoringStore((s) => s.updateSLD);
  const removeSLD = useMonitoringStore((s) => s.removeSLD);
  const projectConfig = useMonitoringStore((s) => s.projectConfig);
  const runtimeStates = useMonitoringStore((s) => s.runtimeStates);
  const triggeredAdvancedRules = useMonitoringStore((s) => s.triggeredAdvancedRules);
  const selectedSiteId = useMonitoringStore((s) => s.selectedSiteId);

  const modalData = useUIStore((s) => s.modalData);
  const closeModal = useUIStore((s) => s.closeModal);

  const datapoints = projectConfig?.datapoints ?? EMPTY_DATAPOINTS;
  const advancedRules = projectConfig?.advanced_rules ?? EMPTY_ADVANCED_RULES;
  const triggered = triggeredAdvancedRules ?? EMPTY_TRIGGERED;

  // ── Local state (triggers re-renders for UI) ──
  const initialSldId = (modalData?.sldId as string) ?? sldDiagrams[0]?.id ?? null;
  const initialFocusPointId = (modalData?.focusPointId as string) ?? null;
  const [activeDiagramId, setActiveDiagramId] = useState<string | null>(initialSldId);
  const [showSidePanel, setShowSidePanel] = useState(true);
  const [sidePanelTab, setSidePanelTab] = useState<SidePanelTab>('datapoints');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusText, setStatusText] = useState('Ready');
  const [zoomPct, setZoomPct] = useState(100);
  const [isPlacing, setIsPlacing] = useState(false);

  // Multi-view mode
  const [multiViewActive, setMultiViewActive] = useState(false);

  // Point detail popup
  const [detailMapping, setDetailMapping] = useState<SLDPointMapping | null>(null);
  const [detailPos, setDetailPos] = useState<{ x: number; y: number } | null>(null);
  const [detailMetric, setDetailMetric] = useState('');
  const [detailDiagramId, setDetailDiagramId] = useState<string | null>(null);

  // Inline data point edit (rendered locally so the SLD stays open)
  const [editingDpId, setEditingDpId] = useState<string | null>(null);

  // ── DOM refs ──
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coordsRef = useRef<HTMLSpanElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // ── Interaction state (mutable refs — avoids stale closures in native handlers) ──
  const viewRef = useRef({ x: 0, y: 0, scale: 1 });
  const panRef = useRef({ active: false, startX: 0, startY: 0 });
  const pointDragRef = useRef<{
    id: string;
    type: 'datapoint' | 'rule';
    startClientX: number;
    startClientY: number;
    origImgX: number;
    origImgY: number;
    moved: boolean;
  } | null>(null);
  const placingRef = useRef({ active: false, itemId: null as string | null, type: 'datapoint' as PlacementType });
  const justPlacedRef = useRef(false);

  // ── "Latest values" ref — updated every render, read by native event handlers ──
  const storeRef = useRef({
    sldDiagrams,
    activeDiagramId,
    updateSLD,
    setStatusText,
    setIsPlacing,
    setDetailMapping,
    setDetailPos,
    setDetailMetric,
    setDetailDiagramId,
    closeModal,
  });
  storeRef.current = {
    sldDiagrams,
    activeDiagramId,
    updateSLD,
    setStatusText,
    setIsPlacing,
    setDetailMapping,
    setDetailPos,
    setDetailMetric,
    setDetailDiagramId,
    closeModal,
  };

  // ── Derived ──
  const activeDiagram = sldDiagrams.find((d) => d.id === activeDiagramId) ?? null;
  const mappings = activeDiagram?.dataPointMappings ?? EMPTY_MAPPINGS;
  const ruleMappings = activeDiagram?.advancedRuleMappings ?? EMPTY_RULE_MAPPINGS;

  // ── Transform helpers ──
  const applyTransform = useCallback(() => {
    const { x, y, scale } = viewRef.current;
    const t = `translate(${x}px, ${y}px) scale(${scale})`;
    if (canvasRef.current) canvasRef.current.style.transform = t;
    if (overlayRef.current) overlayRef.current.style.transform = t;
    setZoomPct(Math.round(scale * 100));
  }, []);

  const fitToView = useCallback(() => {
    const img = imageRef.current;
    const container = containerRef.current;
    if (!img || !container) return;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const iw = img.width;
    const ih = img.height;
    if (iw === 0 || ih === 0) return;
    const scale = Math.min(cw / iw, ch / ih) * 0.95;
    viewRef.current = {
      scale,
      x: (cw - iw * scale) / 2,
      y: (ch - ih * scale) / 2,
    };
    applyTransform();
  }, [applyTransform]);

  const focusOnPoint = useCallback(
    (imgX: number, imgY: number) => {
      const container = containerRef.current;
      if (!container) return;
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const scale = 2.5;
      viewRef.current = {
        scale,
        x: cw / 2 - imgX * scale,
        y: ch / 2 - imgY * scale,
      };
      applyTransform();
    },
    [applyTransform],
  );

  const zoomBy = useCallback(
    (delta: number, centerX?: number, centerY?: number) => {
      const v = viewRef.current;
      const oldScale = v.scale;
      const newScale = clamp(oldScale * (1 + delta), 0.05, 10);
      if (centerX !== undefined && centerY !== undefined) {
        v.x = centerX - ((centerX - v.x) / oldScale) * newScale;
        v.y = centerY - ((centerY - v.y) / oldScale) * newScale;
      }
      v.scale = newScale;
      applyTransform();
    },
    [applyTransform],
  );

  // ── Load diagram image ──
  // Accepts optional diagram object to avoid stale-closure issues
  // (e.g. right after addSLD, sldDiagrams hasn't re-rendered yet).
  const loadDiagram = useCallback(
    (diagramId: string, diagramOverride?: SLDDiagram, focusPointId?: string) => {
      const diagram = diagramOverride ?? sldDiagrams.find((d) => d.id === diagramId);
      if (!diagram) return;
      setActiveDiagramId(diagramId);
      setStatusText('Loading diagram...');
      setDetailMapping(null);

      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0);
        }
        // If a focus point is specified, zoom to it; otherwise fit whole diagram
        const mapping = focusPointId
          ? diagram.dataPointMappings?.find((m) => m.dataPointId === focusPointId)
          : null;
        if (mapping) {
          focusOnPoint(mapping.x, mapping.y);
        } else {
          fitToView();
        }
        setStatusText(`Loaded: ${diagram.name} (${img.width}\u00d7${img.height})`);
      };
      img.onerror = () => {
        setStatusText('Error loading diagram image');
        imageRef.current = null;
      };
      img.src = diagram.imageUrl;
    },
    [sldDiagrams, fitToView, focusOnPoint],
  );

  // Load initial diagram
  useEffect(() => {
    if (activeDiagramId) loadDiagram(activeDiagramId, undefined, initialFocusPointId ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ────────────────────────────────────────────────────────────
  // Native event listeners — wheel, window mousemove/mouseup, click capture
  // Using refs so handlers always read latest state without stale closures.
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // ── Wheel zoom (passive: false so preventDefault works) ──
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const v = viewRef.current;
      const oldScale = v.scale;
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newScale = clamp(oldScale * (1 + delta), 0.05, 10);
      const rect = container.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      v.x = cx - ((cx - v.x) / oldScale) * newScale;
      v.y = cy - ((cy - v.y) / oldScale) * newScale;
      v.scale = newScale;
      const t = `translate(${v.x}px, ${v.y}px) scale(${v.scale})`;
      if (canvasRef.current) canvasRef.current.style.transform = t;
      if (overlayRef.current) overlayRef.current.style.transform = t;
      setZoomPct(Math.round(v.scale * 100));
    };
    container.addEventListener('wheel', onWheel, { passive: false });

    // ── Window mousemove — pan, point drag, coord display ──
    const onMouseMove = (e: MouseEvent) => {
      // Update coords display (direct DOM, no React state)
      const img = imageRef.current;
      if (img) {
        const rect = container.getBoundingClientRect();
        const v = viewRef.current;
        const imgX = Math.round((e.clientX - rect.left - v.x) / v.scale);
        const imgY = Math.round((e.clientY - rect.top - v.y) / v.scale);
        if (coordsRef.current) {
          if (imgX >= 0 && imgY >= 0 && imgX <= img.width && imgY <= img.height) {
            coordsRef.current.textContent = `${imgX}, ${imgY}`;
          } else {
            coordsRef.current.textContent = '';
          }
        }
      }

      // ── Point dragging ──
      if (pointDragRef.current) {
        const pd = pointDragRef.current;
        const screenDx = Math.abs(e.clientX - pd.startClientX);
        const screenDy = Math.abs(e.clientY - pd.startClientY);
        if (screenDx > DRAG_THRESHOLD || screenDy > DRAG_THRESHOLD) {
          pd.moved = true;
        }
        if (pd.moved) {
          const v = viewRef.current;
          const dx = (e.clientX - pd.startClientX) / v.scale;
          const dy = (e.clientY - pd.startClientY) / v.scale;
          const newX = pd.origImgX + dx;
          const newY = pd.origImgY + dy;
          // Imperatively update DOM for smooth dragging (no React re-render)
          const selector =
            pd.type === 'datapoint'
              ? `.sld-point[data-point-id="${pd.id}"]`
              : `.sld-point[data-rule-id="${pd.id}"]`;
          const el = document.querySelector(selector) as HTMLElement | null;
          if (el) {
            el.style.left = `${newX}px`;
            el.style.top = `${newY}px`;
            el.classList.add('sld-point--dragging');
          }
        }
        return; // don't pan while dragging a point
      }

      // ── Panning ──
      if (panRef.current.active) {
        viewRef.current.x = e.clientX - panRef.current.startX;
        viewRef.current.y = e.clientY - panRef.current.startY;
        const v = viewRef.current;
        const t = `translate(${v.x}px, ${v.y}px) scale(${v.scale})`;
        if (canvasRef.current) canvasRef.current.style.transform = t;
        if (overlayRef.current) overlayRef.current.style.transform = t;
      }
    };
    window.addEventListener('mousemove', onMouseMove);

    // ── Window mouseup — end pan, commit point drag or detect click ──
    const onMouseUp = (e: MouseEvent) => {
      // End pan
      if (panRef.current.active) {
        panRef.current.active = false;
        container.classList.remove('dragging');
      }

      // End point drag
      if (pointDragRef.current) {
        const pd = pointDragRef.current;
        const store = storeRef.current;
        const diagram = store.sldDiagrams.find((d) => d.id === store.activeDiagramId);

        if (pd.moved && diagram) {
          // Commit drag: compute final position and update store
          const v = viewRef.current;
          const dx = (e.clientX - pd.startClientX) / v.scale;
          const dy = (e.clientY - pd.startClientY) / v.scale;

          if (pd.type === 'datapoint') {
            const newMappings = diagram.dataPointMappings.map((m) =>
              m.dataPointId === pd.id
                ? { ...m, x: pd.origImgX + dx, y: pd.origImgY + dy }
                : m,
            );
            store.updateSLD(diagram.id, { dataPointMappings: newMappings });
          } else {
            const newRuleMappings = (diagram.advancedRuleMappings ?? []).map((m) =>
              m.advancedRuleId === pd.id
                ? { ...m, x: pd.origImgX + dx, y: pd.origImgY + dy }
                : m,
            );
            store.updateSLD(diagram.id, { advancedRuleMappings: newRuleMappings });
          }
        } else if (!pd.moved && pd.type === 'datapoint' && diagram) {
          // Was a click (no drag) — show detail popup
          const mapping = diagram.dataPointMappings.find((m) => m.dataPointId === pd.id);
          if (mapping) {
            store.setDetailMapping(mapping);
            store.setDetailPos({ x: e.clientX, y: e.clientY });
            store.setDetailMetric(mapping.displayMetric ?? '');
            store.setDetailDiagramId(diagram.id);
          }
        }

        // Clean up
        document.querySelectorAll('.sld-point--dragging').forEach((el) => {
          el.classList.remove('sld-point--dragging');
        });
        pointDragRef.current = null;
      }
    };
    window.addEventListener('mouseup', onMouseUp);

    // ── Container click (CAPTURE phase) — for point placement ──
    const onContainerClick = (e: MouseEvent) => {
      // Block click right after placement to prevent double-fire
      if (justPlacedRef.current) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();
        return;
      }

      // Not placing? Let event through normally
      if (!placingRef.current.active || !placingRef.current.itemId) return;

      // We ARE placing — intercept the click
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

      const store = storeRef.current;
      const diagram = store.sldDiagrams.find((d) => d.id === store.activeDiagramId);
      if (!diagram) return;

      const rect = container.getBoundingClientRect();
      const v = viewRef.current;
      const imgX = (e.clientX - rect.left - v.x) / v.scale;
      const imgY = (e.clientY - rect.top - v.y) / v.scale;
      const img = imageRef.current;

      if (img && imgX >= 0 && imgY >= 0 && imgX <= img.width && imgY <= img.height) {
        const { itemId, type } = placingRef.current;
        if (type === 'datapoint') {
          const newMapping: SLDPointMapping = { dataPointId: itemId!, x: imgX, y: imgY };
          store.updateSLD(diagram.id, {
            dataPointMappings: [...diagram.dataPointMappings, newMapping],
          });
        } else {
          const newMapping: SLDAdvancedRuleMapping = { advancedRuleId: itemId!, x: imgX, y: imgY };
          store.updateSLD(diagram.id, {
            advancedRuleMappings: [...(diagram.advancedRuleMappings ?? []), newMapping],
          });
        }
        store.setStatusText('Point placed');
      }

      // Reset placement state
      justPlacedRef.current = true;
      placingRef.current = { active: false, itemId: null, type: 'datapoint' };
      store.setIsPlacing(false);

      // Clear justPlaced flag after event loop settles
      setTimeout(() => {
        justPlacedRef.current = false;
      }, 150);
    };
    container.addEventListener('click', onContainerClick, true); // capture phase

    return () => {
      container.removeEventListener('wheel', onWheel);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('click', onContainerClick, true);
    };
  }, []); // empty deps — all mutable state accessed via refs

  // ── Container mousedown (start pan) — React handler is fine here ──
  const handleContainerMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if (placingRef.current.active) return; // placement uses click, not drag
    if (pointDragRef.current) return; // point drag in progress
    panRef.current = {
      active: true,
      startX: e.clientX - viewRef.current.x,
      startY: e.clientY - viewRef.current.y,
    };
    containerRef.current?.classList.add('dragging');
  }, []);

  // ── Point mousedown (start point drag) ──
  const handlePointMouseDown = useCallback(
    (e: React.MouseEvent, id: string, type: 'datapoint' | 'rule', imgX: number, imgY: number) => {
      if (placingRef.current.active) return;
      if (e.button !== 0) return;
      e.stopPropagation(); // prevent container mousedown (pan)
      e.preventDefault();
      pointDragRef.current = {
        id,
        type,
        startClientX: e.clientX,
        startClientY: e.clientY,
        origImgX: imgX,
        origImgY: imgY,
        moved: false,
      };
    },
    [],
  );

  // ── Placement start/cancel ──
  const handleStartPlacement = useCallback((itemId: string, type: PlacementType) => {
    placingRef.current = { active: true, itemId, type };
    setIsPlacing(true);
    setDetailMapping(null);
    setStatusText('Click on diagram to place point');
  }, []);

  const handleCancelPlacement = useCallback(() => {
    placingRef.current = { active: false, itemId: null, type: 'datapoint' };
    setIsPlacing(false);
    setStatusText('Placement cancelled');
  }, []);

  // ── Close handler ──
  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      closeModal();
    },
    [closeModal],
  );

  // ── Detail popup actions ──
  // Resolve the diagram for detail actions: multi-view uses detailDiagramId, single uses activeDiagram
  const detailDiagram = (detailDiagramId ? sldDiagrams.find((d) => d.id === detailDiagramId) : null) ?? activeDiagram;

  const handleDetailSaveMetric = useCallback(() => {
    if (!detailMapping || !detailDiagram) return;
    const newMappings = detailDiagram.dataPointMappings.map((m) =>
      m.dataPointId === detailMapping.dataPointId ? { ...m, displayMetric: detailMetric || undefined } : m,
    );
    updateSLD(detailDiagram.id, { dataPointMappings: newMappings });
    setDetailMapping(null);
  }, [detailMapping, detailMetric, detailDiagram, updateSLD]);

  const handleRemoveFromDiagram = useCallback(
    (dpId: string) => {
      if (!detailDiagram) return;
      updateSLD(detailDiagram.id, {
        dataPointMappings: detailDiagram.dataPointMappings.filter((m) => m.dataPointId !== dpId),
      });
      setDetailMapping(null);
    },
    [detailDiagram, updateSLD],
  );

  const handleRemoveRuleFromDiagram = useCallback(
    (ruleId: string) => {
      if (!activeDiagram) return;
      updateSLD(activeDiagram.id, {
        advancedRuleMappings: (activeDiagram.advancedRuleMappings ?? []).filter((m) => m.advancedRuleId !== ruleId),
      });
    },
    [activeDiagram, updateSLD],
  );

  const handleLocateOnMap = useCallback(
    (dpId: string) => {
      const dp = datapoints.find((d) => d.id === dpId);
      if (!dp?.coordinates) return;
      closeModal();
      useMapStore.getState().flyTo(dp.coordinates as [number, number], 20);
    },
    [datapoints, closeModal],
  );

  // ── Save handler ──
  const handleSave = useCallback(async () => {
    const { meta, markSaved, setSaveStatus } = useProjectStore.getState();
    if (!meta.siteId || !useAuthStore.getState().isAuthenticated) {
      setStatusText('Cannot save — no project loaded');
      return;
    }
    setStatusText('Saving...');
    setSaveStatus('saving');
    try {
      const fullData = useProjectStore.getState().exportProject();
      await saveProject(meta.siteId, fullData as any);
      markSaved();
      setSaveStatus('saved');
      setStatusText('Project saved');
      setTimeout(() => {
        if (useProjectStore.getState().meta.saveStatus === 'saved') {
          useProjectStore.getState().setSaveStatus('idle');
        }
      }, 3000);
    } catch (err) {
      console.error('[SLD] Save error:', err);
      setSaveStatus('error');
      setStatusText('Save failed: ' + (err instanceof Error ? err.message : String(err)));
    }
  }, []);

  // ── Multi-view toggle ──
  const handleToggleMultiView = useCallback(() => {
    setMultiViewActive((v) => !v);
    setDetailMapping(null);
  }, []);

  // ── Multi-view: maximize (open diagram in single view) ──
  const handleMaximizeDiagram = useCallback(
    (diagramId: string) => {
      setMultiViewActive(false);
      loadDiagram(diagramId);
    },
    [loadDiagram],
  );

  // ── Upload handler ──
  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';

      setStatusText('Uploading...');
      try {
        let imageBlob: Blob;

        if (file.type === 'application/pdf') {
          const pdfjsLib = await import('pdfjs-dist');
          // pdfjs-dist v5 uses Uint8Array.toHex() in the worker (Chrome 134+).
          // We create a wrapper blob that polyfills toHex inside the worker
          // before importing the real worker code. pdfjs always creates a
          // module worker ({ type: "module" }) so await import() works.
          const realWorkerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
          const workerWrapper = [
            'if(!Uint8Array.prototype.toHex){Uint8Array.prototype.toHex=function(){return Array.from(this,b=>b.toString(16).padStart(2,"0")).join("")}}',
            `await import("${realWorkerUrl}");`,
          ].join('\n');
          pdfjsLib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(
            new Blob([workerWrapper], { type: 'text/javascript' }),
          );

          const arrayBuf = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuf }).promise;
          const page = await pdf.getPage(1);
          const scale = 2;
          const viewport = page.getViewport({ scale });
          const cvs = document.createElement('canvas');
          cvs.width = viewport.width;
          cvs.height = viewport.height;
          const ctx = cvs.getContext('2d')!;
          await page.render({ canvasContext: ctx, viewport } as any).promise;

          imageBlob = await new Promise<Blob>((resolve, reject) => {
            cvs.toBlob((b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))), 'image/png');
          });
        } else {
          imageBlob = file;
          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });
          const img = new Image();
          await new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = dataUrl;
          });
        }

        let imageUrl: string;
        if (selectedSiteId) {
          const ext = file.name.split('.').pop() || 'png';
          const result = await uploadAsset(imageBlob, selectedSiteId, 'sld', `sld_${Date.now()}.${ext}`);
          imageUrl = result.url;
        } else {
          imageUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(imageBlob);
          });
        }

        const name = file.name.replace(/\.(pdf|png|jpg|jpeg)$/i, '');
        const newDiagram: SLDDiagram = {
          id: crypto.randomUUID(),
          name,
          imageUrl,
          dataPointMappings: [],
        };
        addSLD(newDiagram);
        loadDiagram(newDiagram.id, newDiagram);
        setStatusText(`Uploaded: ${name}`);
      } catch (err) {
        console.error('[SLD] Upload error:', err);
        setStatusText('Upload error: ' + (err instanceof Error ? err.message : String(err)));
      }
    },
    [selectedSiteId, addSLD, loadDiagram],
  );

  // ── Delete diagram ──
  const handleDeleteDiagram = useCallback(() => {
    if (!activeDiagram) return;
    if (!confirm(`Delete diagram "${activeDiagram.name}"?`)) return;
    removeSLD(activeDiagram.id);
    const remaining = sldDiagrams.filter((d) => d.id !== activeDiagram.id);
    if (remaining.length > 0) {
      loadDiagram(remaining[0].id);
    } else {
      setActiveDiagramId(null);
      imageRef.current = null;
    }
  }, [activeDiagram, removeSLD, sldDiagrams, loadDiagram]);

  // ── Side panel data ──
  const placedDpIds = new Set(mappings.map((m) => m.dataPointId));
  const placedRuleIds = new Set(ruleMappings.map((m) => m.advancedRuleId));

  // ── Overlay dimensions ──
  const img = imageRef.current;
  const overlayW = img?.width ?? 0;
  const overlayH = img?.height ?? 0;

  // ────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────

  return (
    <div className="sld-viewer" onMouseDown={(e) => e.stopPropagation()}>
      {/* ── Header Toolbar ── */}
      <SLDHeader
        sldDiagrams={sldDiagrams}
        activeDiagramId={activeDiagramId}
        activeDiagram={activeDiagram}
        zoomPct={zoomPct}
        isPlacing={isPlacing}
        showSidePanel={showSidePanel}
        multiViewActive={multiViewActive}
        hasImage={!!imageRef.current}
        onDiagramChange={(id) => loadDiagram(id)}
        onUploadClick={() => fileInputRef.current?.click()}
        onDeleteDiagram={handleDeleteDiagram}
        onCancelPlacement={handleCancelPlacement}
        onToggleSidePanel={() => setShowSidePanel((v) => !v)}
        onToggleMultiView={handleToggleMultiView}
        onSave={handleSave}
        onFitToView={() => fitToView()}
        onZoomIn={() => zoomBy(0.2)}
        onZoomOut={() => zoomBy(-0.2)}
        onClose={handleClose}
      />

      {/* ── Body ── */}
      <div className="sld-body">
        {/* ── Multi-View Mode ── */}
        {multiViewActive && (
          <MultiViewContainer
            diagrams={sldDiagrams}
            datapoints={datapoints}
            advancedRules={advancedRules}
            runtimeStates={runtimeStates}
            triggeredAdvancedRules={triggered}
            onMaximize={handleMaximizeDiagram}
            onPointClick={(e, mapping, diag) => {
              setDetailMapping(mapping);
              setDetailPos({ x: e.clientX, y: e.clientY });
              setDetailMetric(mapping.displayMetric ?? '');
              setDetailDiagramId(diag.id);
            }}
          />
        )}

        {/* ── Single View Mode (always mounted to preserve refs/events) ── */}
        <div className="sld-single-view" style={{ display: multiViewActive ? 'none' : undefined }}>
          {/* Canvas Area */}
          <div className="sld-canvas-area">
            <div
              ref={containerRef}
              className={`sld-canvas-container${isPlacing ? ' placing-point' : ''}`}
              onMouseDown={handleContainerMouseDown}
            >
              <canvas ref={canvasRef} className="sld-canvas" />

              {/* Points Overlay Layer */}
              <div
                ref={overlayRef}
                className={`sld-overlay-layer${isPlacing ? ' placing-mode' : ''}`}
                style={{ width: overlayW, height: overlayH }}
              >
                {/* Data Point indicators */}
                {mappings.map((mapping) => {
                  const runtime = runtimeStates.get(mapping.dataPointId);
                  const config = datapoints.find((dp) => dp.id === mapping.dataPointId);
                  const state: AlarmState = runtime?.state ?? 'no_data';

                  return (
                    <div
                      key={`dp-${mapping.dataPointId}`}
                      className="sld-point"
                      data-point-id={mapping.dataPointId}
                      style={{ left: mapping.x, top: mapping.y }}
                      onMouseDown={(e) =>
                        handlePointMouseDown(e, mapping.dataPointId, 'datapoint', mapping.x, mapping.y)
                      }
                    >
                      <span className={`sld-point-dot ${stateClass(state)}`} />
                      <span className="sld-point-label">{config?.display_name ?? 'Unknown'}</span>
                      {renderMetricValues(config, runtime, mapping.displayMetric)}
                    </div>
                  );
                })}

                {/* Advanced Rule indicators */}
                {ruleMappings.map((mapping) => {
                  const trig = triggered.find((r) => r.rule_id === mapping.advancedRuleId);
                  const rule = advancedRules.find((r) => r.id === mapping.advancedRuleId);
                  const ruleState = trig ? trig.severity : 'ok';

                  return (
                    <div
                      key={`rule-${mapping.advancedRuleId}`}
                      className="sld-point sld-point--rule"
                      data-rule-id={mapping.advancedRuleId}
                      style={{ left: mapping.x, top: mapping.y }}
                      onMouseDown={(e) =>
                        handlePointMouseDown(e, mapping.advancedRuleId, 'rule', mapping.x, mapping.y)
                      }
                    >
                      <span className={`sld-point-dot sld-point-dot--diamond ${stateClass(ruleState)}`} />
                      <span className="sld-point-label">{rule?.name ?? 'Rule'}</span>
                      {trig && (
                        <span className={`sld-point-value ${stateTextClass(trig.severity)}`}>
                          {trig.severity.toUpperCase()}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* No Diagram Empty State */}
            {!activeDiagram && (
              <div className="sld-no-diagram">
                <div className="sld-no-diagram-content">
                  <div className="sld-no-diagram-icon">&#128202;</div>
                  <h3>No Single Line Diagram</h3>
                  <p>Upload a PDF or image to get started</p>
                  <button className="sld-upload-btn" onClick={() => fileInputRef.current?.click()}>
                    Upload Diagram
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Side Panel ── */}
          {showSidePanel && activeDiagram && (
            <SLDSidePanel
              datapoints={datapoints}
              advancedRules={advancedRules}
              runtimeStates={runtimeStates}
              triggered={triggered}
              placedDpIds={placedDpIds}
              placedRuleIds={placedRuleIds}
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
              sidePanelTab={sidePanelTab}
              onSidePanelTabChange={setSidePanelTab}
              onStartPlacement={handleStartPlacement}
              onRemoveFromDiagram={handleRemoveFromDiagram}
              onRemoveRuleFromDiagram={handleRemoveRuleFromDiagram}
            />
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="sld-footer">
        <span>{statusText}</span>
        <span>
          Coords: <span ref={coordsRef} />
          {' | '}
          Zoom: {zoomPct}%
          {activeDiagram && ` | ${mappings.length} point(s), ${ruleMappings.length} rule(s)`}
          {' | '}
          {sldDiagrams.length} diagram(s)
        </span>
      </div>

      {/* ── Hidden file input ── */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* ── Point Detail Popup ── */}
      {detailMapping && detailPos && (
        <>
          <div className="sld-detail-backdrop" onClick={() => setDetailMapping(null)} />
          <PointDetailPopup
            mapping={detailMapping}
            position={detailPos}
            datapoints={datapoints}
            runtimeStates={runtimeStates}
            advancedRules={advancedRules}
            triggeredAdvancedRules={triggered}
            displayMetric={detailMetric}
            onDisplayMetricChange={setDetailMetric}
            onSave={handleDetailSaveMetric}
            onRemove={handleRemoveFromDiagram}
            onLocateOnMap={handleLocateOnMap}
            onEdit={(dpId) => {
              setEditingDpId(dpId);
            }}
            onClose={() => setDetailMapping(null)}
          />
        </>
      )}

      {/* ── Inline Data Point Editor (stays on top of SLD) ── */}
      {editingDpId && (
        <DataPointConfigModal
          isOpen={true}
          onClose={() => setEditingDpId(null)}
          datapointId={editingDpId}
        />
      )}
    </div>
  );
}



